import {testIdProps} from '@github-ui/test-id-props'
import {createContext, memo, useCallback, useContext, useMemo, useRef, useState} from 'react'

import {isValidBulkAddCompleteEvent, isValidBulkAddProgressEvent} from '../../helpers/alive'
import {useAliveChannel} from '../../hooks/use-alive-channel'
import {useEnabledFeatures} from '../../hooks/use-enabled-features'
import {usePageVisibility} from '../../hooks/use-page-visibility'
import {useHandlePaginatedDataRefresh} from '../data-refresh/use-handle-paginated-data-refresh'
import {useMemexItems} from '../memex-items/use-memex-items'

type BulkAddProgressInfo = {
  requestId: string
  percentage: number | null
  totalItemsAdded: number | null
  totalItemsFailed: number | null
  isComplete: boolean
  initialImport: boolean
}

type BulkAddNotificationsContextType = {
  // Current state of the bulk add operation, or null if no operation is in progress
  bulkAddState: BulkAddProgressInfo | null
  // Initializes tracking for a new bulk add operation with the given request ID
  notifyBulkAddStarted: (requestId: string) => void
  // Clears the current bulk add notification state, effectively setting state to null
  clearNotification: () => void
  // Whether to show a skeleton loader in portions of the UI
  shouldShowSkeleton: boolean
}

export const BulkAddNotificationsContext = createContext<BulkAddNotificationsContextType | null | undefined>(null)

/**
 * Provider that manages bulk add notification state and listens for live updates via the alive channel.
 * Tracks progress and completion of a single bulk add operation at a time. If an instance receives live updates
 * relating to a different requestId, we ignore them (with it being likely that this never happens).
 */
export const BulkAddNotificationsStateProvider = memo(function BulkAddNotificationsStateProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const aliveMessageRef = useRef<HTMLSpanElement | null>(null)
  const [bulkAddState, setBulkAddState] = useState<BulkAddProgressInfo | null>(null)
  const isPageVisible = usePageVisibility()
  const handlePaginatedDataRefresh = useHandlePaginatedDataRefresh().handleRefresh
  const {projects_attach_repository} = useEnabledFeatures()
  const {items} = useMemexItems()

  // Always initialize the state when we get notified that a bulk add has started, so that the current user will
  // see the progress of their own bulk add operation
  const notifyBulkAddStarted = useCallback((requestId: string) => {
    setBulkAddState({
      requestId,
      percentage: 0,
      totalItemsAdded: null,
      totalItemsFailed: null,
      isComplete: false,
      initialImport: false,
    })
  }, [])

  const updateProgress = useCallback((requestId: string, percentage: number) => {
    setBulkAddState(prev => {
      // Initialize the state if we don't already have one we're monitoring
      if (!prev) {
        return {
          requestId,
          percentage,
          totalItemsAdded: null,
          totalItemsFailed: null,
          isComplete: false,
          initialImport: false,
        }
      }
      // Only update progress if the requestId matches the one we're tracking
      if (prev?.requestId === requestId) {
        return {
          ...prev,
          percentage,
        }
      }
      return prev
    })
  }, [])

  const completeJob = useCallback(
    (requestId: string, totalItemsAdded: number, totalItemsFailed: number, initialImport: boolean) => {
      setBulkAddState(prev => {
        if (prev?.requestId === requestId) {
          return {
            ...prev,
            totalItemsAdded,
            totalItemsFailed,
            isComplete: true,
            initialImport,
          }
        }
        return prev
      })
    },
    [],
  )

  const clearNotification = useCallback(() => {
    setBulkAddState(null)
  }, [])

  const shouldShowSkeleton = useMemo(() => {
    return Boolean(
      // If there is some local bulk-add state
      bulkAddState &&
        // If it is complete, we should only show the skeleton if items have been added by the bulk-add process
        (!bulkAddState.isComplete || (bulkAddState.totalItemsAdded ?? 0) > 0) &&
        // If there aren't any items in the local state yet
        items.length === 0 &&
        projects_attach_repository,
    )
  }, [bulkAddState, items.length, projects_attach_repository])

  const [attrs] = useAliveChannel(aliveMessageRef, 'message', detail => {
    if (isValidBulkAddProgressEvent(detail.data)) {
      const {percentage, requestId} = detail.data

      if (requestId) {
        updateProgress(requestId, percentage)
      }
    }

    if (isValidBulkAddCompleteEvent(detail.data)) {
      const {totalItemsAdded = 0, totalItemsFailed = 0, invalidateQueryCache, requestId, initialImport} = detail.data

      // If the page isn't visible, we skip the data refresh here, as it will be done when the page becomes visible again
      if (invalidateQueryCache && isPageVisible) {
        handlePaginatedDataRefresh()
      }

      if (requestId) {
        completeJob(requestId, totalItemsAdded, totalItemsFailed, initialImport ?? false)
      }
    }
  })

  const contextValue = useMemo<BulkAddNotificationsContextType>(
    () => ({
      bulkAddState: projects_attach_repository ? bulkAddState : null,
      notifyBulkAddStarted,
      clearNotification,
      shouldShowSkeleton,
    }),
    [bulkAddState, notifyBulkAddStarted, clearNotification, shouldShowSkeleton, projects_attach_repository],
  )

  return (
    <BulkAddNotificationsContext.Provider value={contextValue}>
      {projects_attach_repository && (
        <span {...attrs} ref={aliveMessageRef} hidden {...testIdProps('bulk-add-notifications-listener')} />
      )}
      {children}
    </BulkAddNotificationsContext.Provider>
  )
})

export function useBulkAddNotifications() {
  const context = useContext(BulkAddNotificationsContext)

  if (!context) {
    throw new Error('useBulkAddNotifications must be used within a BulkAddNotificationsStateProvider.')
  }

  return context
}

try{ BulkAddNotificationsContext.displayName ||= 'BulkAddNotificationsContext' } catch {}
try{ BulkAddNotificationsStateProvider.displayName ||= 'BulkAddNotificationsStateProvider' } catch {}